/**
 * WorkstationPlansCard — los planes de trabajo que afectan a ESTE equipo.
 *
 * En la ficha del ordenador el técnico tiene que ver, sin ir a otra pantalla,
 * qué tareas del plan global le faltan a esa máquina y **desde cuándo** cumple
 * las que ya están hechas. Si la empresa no tiene planes, la tarjeta no se
 * pinta: no se ensucia la ficha con un hueco vacío.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  MinusCircleIcon,
  XCircleIcon,
  NoSymbolIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import HelpDot from '../academy/HelpDot';
import './WorkPlans.css';

const ICONS = {
  done: CheckCircleIcon,
  pending: MinusCircleIcon,
  failed: XCircleIcon,
  skipped: NoSymbolIcon,
};

const COLORS = {
  done: 'var(--green-600, #16a34a)',
  pending: 'var(--text-muted)',
  failed: 'var(--red-600, #dc2626)',
  skipped: 'var(--text-muted)',
};

function fmt(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function WorkstationPlansCard({ companyId, ciType = 'workstation', ciId }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    if (!companyId || !ciId) { setLoading(false); return; }
    try {
      const data = await api.getWorkPlansForCi(companyId, ciType, ciId);
      // Un plan sin pasos todavía no dice nada del equipo: no se pinta.
      setPlans((data || []).filter((p) => Array.isArray(p.steps) && p.steps.length > 0));
    } catch {
      // La tarjeta es informativa: si falla no debe romper la ficha.
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, ciType, ciId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading || plans.length === 0) return null;

  return (
    <div className="sf2-card">
      <h3>
        <ClipboardDocumentCheckIcon style={{ width: 18, height: 18, marginRight: 6, verticalAlign: '-3px' }} />
        Planes de trabajo
        <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>
          lo que falta a este equipo
        </span>
        <HelpDot topic="planes.workstation" label="Ayuda: planes de trabajo del equipo" />
      </h3>
      {plans.map((plan) => {
        const hechos = plan.steps.filter((s) => s.state === 'done' || s.state === 'skipped').length;
        const total = plan.steps.length;
        const pct = total ? Math.round((hechos / total) * 100) : 0;
        return (
          <div key={plan.id} className="wp-ci-plan">
            <div className="wp-ci-plan-head">
              <span className="wp-ci-plan-title">{plan.title}</span>
              <div className="wp-progress" style={{ minWidth: 140 }}>
                <div className="wp-progress-bar">
                  <div
                    className={`wp-progress-fill ${pct === 100 ? 'is-done' : ''}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="wp-progress-text">{hechos}/{total}</span>
              </div>
            </div>
            {plan.steps.map((step) => {
              const Icono = ICONS[step.state] || ICONS.pending;
              const ev = step.evidence || {};
              const detalle = step.check_type === 'inventory' && ev.field
                ? `${ev.field}: ${ev.observed == null ? '—' : ev.observed}`
                : null;
              return (
                <div key={step.id} className="wp-ci-step">
                  <Icono style={{ color: COLORS[step.state] || COLORS.pending }} />
                  <span>{step.title}</span>
                  {step.doc && (
                    <a
                      className="wp-ci-step-doc"
                      href={`/documentation/${step.doc.id}`}
                      target="_blank"
                      rel="noreferrer"
                      title={`Cómo hacerlo: ${step.doc.title}`}
                    >
                      <DocumentTextIcon />
                    </a>
                  )}
                  {detalle && <span className="wp-ci-step-obs">{detalle}</span>}
                  {step.changed_at && (step.state === 'done' || step.state === 'skipped') && (
                    <span className="wp-ci-step-obs" style={{ marginLeft: detalle ? 10 : 'auto' }}>
                      desde {fmt(step.changed_at)}
                    </span>
                  )}
                  {step.marked_by && (
                    <span className="wp-ci-step-obs" title="Quién lo marcó">
                      {step.marked_by}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
